import React, {
  useCallback, useEffect, useMemo, useRef
} from 'react';

import AutoSizer from 'react-virtualized-auto-sizer';
import InfiniteLoader from 'react-window-infinite-loader';

import { StyledOcticon, Timeline } from '@primer/react';
import { ClockIcon } from '@primer/octicons-react';
import { VariableSizeList } from 'react-window';

import parseISO from 'date-fns/parseISO';

import reverse from 'lodash/reverse';
import sortBy from 'lodash/sortBy';


import ContextualVersionHistoryItem, { LoadingVersionItem } from './VersionHistoryItem';

import CollapsibleCardView from '../common/CardView/CollapsibleCardView';
import ErrorLabel from '../common/Labels/ErrorLabel';
import LoadingSpinner from '../common/LoadingSpinner/LoadingSpinner';
import VersionIcon from '../common/icons/VersionIcon';
import VerticalGradientOverlay from '../common/overlays/VerticalGradientOverlay';

import Button from '../common/Button/Button';
import Label from '../common/Labels/Label';
import ModalOverlay from '../common/ModalOverlay/ModalOverlay';

import { BasicClipboardElement } from '../common/ClipboardLabel/BasicClipboardElement';
import CardHeader from '../common/Labels/CardHeader';
import CardView from '../common/CardView/CardView';


import useClientContext from '../contexts/ClientData/ClientData';
import { usePipelinePublishPackageDetails } from '../pipelines/PipelineDetail/tabs/PipelinePublishPackageDetails'
import { usePublishPipelineData } from './PublishPipelineContext';

const sortVersionHistoryData = (versionHistory = []) => versionHistory
  ? reverse(sortBy(versionHistory, versionItem => parseISO(versionItem.createdAt)))
  : versionHistory;

const VersionHistoryItemWithRef = React.forwardRef((props, ref) => (
  <div ref={ref}>
    <ContextualVersionHistoryItem {...props} />
  </div>
))

const LoadingVersionItemWithRef = React.forwardRef((props, ref) => (
  <div ref={ref}>
    <LoadingVersionItem {...props} />
  </div>
))

const ActivityRow = ({ index, style, data }) => {
  const {
    modelID,
    sortedVersionHistory,
    listRef,
    rowHeights,
    isItemLoaded,
    showCondensed,
    isEdgePublishingEnabled,
    pipelineIdName,
    pipelineId,
    engineConfig,
    modelConfigs,
    listOfPublishesForPipeline,
    handlePipelinePublishStatusFetch,
    publishRequest,
    setNewPublish
  } = data;
  const rowRef = useRef({});
  const versionValue = modelID ? sortedVersionHistory[index].modelVersion : sortedVersionHistory[index].version;

  const setRowHeight = useCallback((rowIndex, size) => {
    listRef?.current?.resetAfterIndex(0);
    rowHeights.current = { ...rowHeights.current, [rowIndex]: size }
  }, [listRef, rowHeights]);

  useEffect(() => {
    if (rowRef.current) {
      setRowHeight(index, rowRef.current.clientHeight);
    }
  }, [index, setRowHeight]);


  if (!isItemLoaded(index)) {
    return <LoadingVersionItemWithRef ref={rowRef} condensed={showCondensed} />
  }

  return (
    <div style={style}>
      <VersionHistoryItemWithRef
        ref={rowRef}
        key={versionValue}
        entry={sortedVersionHistory[index]}
        condensed={showCondensed}
        modelID={modelID}
        pipelineIdName={pipelineIdName}
        pipelineId={pipelineId}
        engineConfig={engineConfig}
        modelConfigs={modelConfigs}
        listOfPublishesForPipeline={listOfPublishesForPipeline}
        handlePipelinePublishStatusFetch={handlePipelinePublishStatusFetch}
        publishRequest={publishRequest}
        setNewPublish={setNewPublish}
        isEdgePublishingEnabled={isEdgePublishingEnabled}
      />
    </div>
  )
};

const ActivityTimeline = ({
  width,
  modelID,
  sortedVersionHistory,
  listRef,
  rowHeights,
  isItemLoaded,
  showCondensed,
  pipelineIdName,
  pipelineId,
  itemCount,
  onLoadMoreVersions,
  height,
  getRowHeight,
  engineConfig,
  modelConfigs,
  listOfPublishesForPipeline,
  handlePipelinePublishStatusFetch,
  publishRequest,
  isEdgePublishingEnabled,
  setNewPublish
}) => {
  if (!sortedVersionHistory?.length) {
    return null;
  }

  return (
    <InfiniteLoader
      isItemLoaded={isItemLoaded}
      itemCount={itemCount}
      loadMoreItems={onLoadMoreVersions}
      minimumBatchSize={5}
      threshold={5}
    >
      {({ onItemsRendered, ref }) => (
        <VariableSizeList
          ref={(list) => {
            // Pass List ref through to InfiniteLoader
            ref(list);

            // And store a copy for yourself.
            listRef.current = list;
          }}
          height={height}
          onItemsRendered={onItemsRendered}
          itemCount={sortedVersionHistory.length}
          estimatedItemSize={106}
          itemSize={getRowHeight}
          width={width}
          itemData={{
            modelID,
            sortedVersionHistory,
            listRef,
            rowHeights,
            isItemLoaded,
            showCondensed,
            isEdgePublishingEnabled,
            pipelineIdName,
            pipelineId,
            engineConfig,
            modelConfigs,
            listOfPublishesForPipeline,
            handlePipelinePublishStatusFetch,
            publishRequest,
            setNewPublish
          }}
        >
          {ActivityRow}
        </VariableSizeList>
      )}
    </InfiniteLoader>
  )
};

export const VersionHistoryTimeline = ({
  versionHistoryError,
  areVersionsLoading,
  canLoadMoreVersions,
  onLoadMoreVersions,
  versionHistory,
  modelID,
  pipelineIdName,
  pipelineId,
  engineConfig = {},
  modelConfigs = [],
  timelineTitle = 'Version History',
  height = 480,
  showCondensed = false,
  pipelineChart = null,
  pipelineLocation = null,
  engineUrl = null,
  showPublishDetails = false,
  isEdgePublishingEnabled = false,
  disableScrollFading = false,
  handleCloseModal = () => {},
  listOfPublishesForPipeline = [],
  handlePipelinePublishStatusFetch= () => {},
  publishRequest = () => {},
  setNewPublish = () => {}
}) => {
  const listRef = useRef({});
  const rowHeights = useRef({});

  const sortedVersionHistory = useMemo(() => {
    if (!versionHistory?.length) {
      return null;
    }

    return [
      ...sortVersionHistoryData(versionHistory),
    ];
  }, [versionHistory]);

  const versionHistoryLength = sortedVersionHistory?.length || 0;

  // If there are more items to be loaded then add an extra row to hold a loading indicator.
  const itemCount = canLoadMoreVersions ? versionHistoryLength + 1 : versionHistoryLength;

  // Every row is loaded except for our loading indicator row.
  const isItemLoaded = index => !canLoadMoreVersions || index < versionHistoryLength;

  const noDataMessage = versionHistoryError
    ? `There was an error retrieving version history${modelID ? ' for this model' : pipelineIdName ? ' for this pipeline' : ''}...`
    : `No versions to show${modelID ? ' for this model' : pipelineIdName ? ' for this pipeline' : ''}`;

  const getRowHeight = useCallback((index) => {
    const rowHeight = rowHeights?.current[index]
    return rowHeight || 106;
  }, [rowHeights]);

  if (areVersionsLoading) {
    return <LoadingSpinner animationKey="version-log-loader" useOverlay />;
  }

  return (
    <CollapsibleCardView
      disableFullHeight
      headerText={timelineTitle} icon={VersionIcon}
      fixedHeight="400px"
    >
      {!sortedVersionHistory?.length ? (
        <div className="p-4 flex-grow flex flex-col justify-center items-center">
          <StyledOcticon
            className="mb-2 text-brand"
            icon={ClockIcon}
            size={64}
          />
          <ErrorLabel>{noDataMessage}</ErrorLabel>
        </div>
      ) : (
        <div className="overflow-scroll px-6 flex-grow">
          {/* This zIndex override creates a stacking context, which prevents the Overlays's zIndex: 20 from appearing over root components. */}
          <Timeline sx={{ zIndex: 1 }}>
            {
              !disableScrollFading && (
                <VerticalGradientOverlay className="top-0 left-0 right-0" fromDirection="b" />
              )
            }
            <AutoSizer disableHeight>
              {({ width }) => (
                <ActivityTimeline
                  width={width}
                  modelID={modelID}
                  sortedVersionHistory={sortedVersionHistory}
                  listRef={listRef}
                  rowHeights={rowHeights}
                  isItemLoaded={isItemLoaded}
                  showCondensed={showCondensed}
                  pipelineIdName={pipelineIdName}
                  pipelineId={pipelineId}
                  itemCount={itemCount}
                  onLoadMoreVersions={onLoadMoreVersions}
                  height={height}
                  getRowHeight={getRowHeight}
                  engineConfig={engineConfig}
                  modelConfigs={modelConfigs}
                  listOfPublishesForPipeline={listOfPublishesForPipeline}
                  handlePipelinePublishStatusFetch={handlePipelinePublishStatusFetch}
                  publishRequest={publishRequest}
                  setNewPublish={setNewPublish}
                  isEdgePublishingEnabled={isEdgePublishingEnabled}
                />
              )}
            </AutoSizer>
            {
              !disableScrollFading && (
                <VerticalGradientOverlay className="bottom-0 left-0 right-0" fromDirection="t" />
              )
            }
          </Timeline>
        </div>
      )}
      <ModalOverlay animationKey="publish-pipeline-details" active={showPublishDetails}>
        <div style={{ width: '600px', height: '300px' }}>
          <CardView className="flex flex-col justify-evenly relative pt-0">
            <div className="flex justify-between items-center mb-2">
              <CardHeader size="2xl">Pipeline Information</CardHeader>
              <Button
                variant="close"
                iconSize={24}
                textColor="border-dark"
                hoverTextColor="border-darker"
                activeTextColor="info"
                onClick={() => handleCloseModal()}
              />
            </div>
            <Label>
              <Label bold>Pipeline location: </Label>
              {pipelineLocation}
              <BasicClipboardElement url={pipelineLocation} />
            </Label>
            <Label>
              <Label bold>Pipeline chart: </Label>
              {pipelineChart}
              <BasicClipboardElement url={pipelineChart} />
            </Label>
            <Label>
              <Label bold>Engine url: </Label>
              {engineUrl}
              <BasicClipboardElement url={engineUrl} />
            </Label>
          </CardView>
        </div>
      </ModalOverlay>
    </CollapsibleCardView>
  );
};

const ContextualVersionHistoryTimeline = ({
  versionHistoryError,
  areVersionsLoading,
  canLoadMoreVersions,
  onLoadMoreVersions,
  versionHistory,
  modelID,
  pipelineIdName,
  pipelineId,
  engineConfig = {},
  modelConfigs = [],
  timelineTitle = 'Version History',
  height = 480,
  showCondensed = false,
}) => {
  const { isEdgePublishingEnabled } = useClientContext();

  const {
    pipelineChart,
    pipelineLocation,
    engineUrl,
    showPublishDetails, 
    handleCloseModal,
    handlePipelinePublishStatusFetch
  } = usePipelinePublishPackageDetails();

  const {
    listOfPublishesForPipeline, 
    publishRequest,
    setNewPublish
  } = usePublishPipelineData();

  return (
    <VersionHistoryTimeline
      versionHistoryError={versionHistoryError}
      areVersionsLoading={areVersionsLoading}
      canLoadMoreVersions={canLoadMoreVersions}
      onLoadMoreVersions={onLoadMoreVersions}
      versionHistory={versionHistory}
      modelID={modelID}
      pipelineIdName={pipelineIdName}
      pipelineId={pipelineId}
      engineConfig={engineConfig}
      modelConfigs={modelConfigs}
      timelineTitle={timelineTitle}
      height={height}
      showCondensed={showCondensed}
      pipelineChart={pipelineChart}
      pipelineLocation={pipelineLocation}
      engineUrl={engineUrl}
      showPublishDetails={showPublishDetails}
      handleCloseModal={handleCloseModal}
      listOfPublishesForPipeline={listOfPublishesForPipeline}
      handlePipelinePublishStatusFetch={handlePipelinePublishStatusFetch}
      publishRequest={publishRequest}
      setNewPublish={setNewPublish}
      isEdgePublishingEnabled={isEdgePublishingEnabled}
      disableScrollFading
    />
  )
}

export default ContextualVersionHistoryTimeline
